"use client";

import { useId } from "react";
import { useSilo } from "@/providers/global-data.provider";
import { PieChart, Pie, Sector, Cell } from "recharts";
import {
  ChartConfig,
} from "@/components/chart";
import theme from "@/theme/theme";

// Function to prepare chart data
const prepareChartData = (usedSpace: number, freeSpace: number) => {
  // Handle case where both values are very small (might be due to conversion from bytes)
  if (usedSpace < 0.01 && freeSpace < 0.01) {
    return [
      { storage: "used", value: 1, fill: theme.colors.primary.hex },
      { storage: "free", value: 99, fill: "#64748b" },
    ];
  }

  return [
    { storage: "used", value: usedSpace, fill: theme.colors.primary.hex },
    { storage: "free", value: freeSpace, fill: "#64748b" },
  ];
};

// Chart configuration
const chartConfig = {
  value: {
    label: "Storage",
  },
  used: {
    label: "Used Space",
    color: theme.colors.primary.hex,
  },
  free: {
    label: "Free Space",
    color: "#64748b",
  },
} satisfies ChartConfig;

export function StorageChart() {
  const id = useId();
  const { usedSpace, freeSpace, isLoading } = useSilo();

  // Prepare chart data
  const data = prepareChartData(usedSpace, freeSpace);

  // Show loading state if data is loading
  if (isLoading) {
    return (
      <div className="h-[300px] w-full flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#FF8A3C] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // If both values are 0, show a message
  if (usedSpace === 0 && freeSpace === 0) {
    return (
      <div className="h-[300px] w-full flex items-center justify-center">
        <div className="text-muted-foreground">No storage data available</div>
      </div>
    );
  }

  return (
    <div className="h-[300px] w-full">
      <div className="mx-auto aspect-square max-h-[300px]">
        <PieChart width={300} height={300}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="storage"
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            fill="#8884d8"
            paddingAngle={2}
            // activeIndex={0}
            // activeShape={({ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }) => (
            //   <g>
            //     <Sector
            //       cx={cx}
            //       cy={cy}
            //       innerRadius={innerRadius}
            //       outerRadius={outerRadius + 10}
            //       startAngle={startAngle}
            //       endAngle={endAngle}
            //       fill={fill}
            //     />
            //   </g>
            // )}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
        </PieChart>
      </div>
      <div className="flex justify-center gap-6 mt-4">
        {data.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="h-3 w-3 rounded-full"
              style={{ backgroundColor: entry.fill }}
            />
            <span className="text-sm">
              {entry.storage === "used" ? "Used Space" : "Free Space"}:
              <span className="font-medium">{parseFloat(entry.value.toFixed(2)).toString()} TB</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
